/*
 * Copyright (c) Microsoft Corporation. All rights reserved. Licensed under the MIT license.
 * See LICENSE in the project root for license information.
 */

import { dialogFallback } from "./dialog";
import { callMiddleTier } from "./middle-tier-calls";
import { handleClientSideErrors } from "./error-handler";
// import { logPromise } from "./promise-helper";

/* global OfficeRuntime */
/* global window */ // for delay
/* global console */

// clientside

let retryGetMiddletierToken = 0;

export function getMiddleTierData(endpoint: string, jsonBody?: string): Promise<any> {
  return new Promise((resolve, reject) => {
    OfficeRuntime.auth
      .getAccessToken({
        allowSignInPrompt: true,
        allowConsentPrompt: true,
        // forMSGraphAccess: true, // sideloading
      })
      // intercept call with logging promise
      // .then(logPromise)
      .then((middletierToken) => callMiddleTier(middletierToken, endpoint, jsonBody ? "POST" : "GET", jsonBody))
      .then((response) => {
        if (!response) {
          reject(new Error("Middle tier didn't respond"));
          return;
        }

        if (response.claims) {
          return OfficeRuntime.auth
            .getAccessToken({ authChallenge: response.claims })
            .then((mfaMiddletierToken) =>
              callMiddleTier(mfaMiddletierToken, endpoint, jsonBody ? "POST" : "GET", jsonBody)
            );
        }

        return response;
      })
      .then((response) => {
        if (response.error) {
          return handleAADErrors(response, endpoint, jsonBody);
        }

        return response;
      })
      .then((finalResponse) => {
        resolve(finalResponse);
      })
      .catch((exception) => {
        //retry once if code is 13013 or we get a network error (happens for http3 with embedded edge)
        if ((!exception.code || (exception && exception.code === 13013)) && retryGetMiddletierToken <= 0) {
          console.log("Retrying getMiddleTierData for 13013 error.");
          retryGetMiddletierToken++;
          //wait 4 seconds before retrying
          window.setTimeout(() => {
            getMiddleTierData(endpoint, jsonBody)
              .then((userData) => resolve(userData))
              .catch((error) => reject(error));
          }, 4000);
        } else if (handleClientSideErrors(exception)) {
          dialogFallback()
            .then((response) => resolve(response))
            .catch((error) => reject(error));
        } else {
          reject("EXCEPTION: " + JSON.stringify(exception));
        }
      });
  });
}

function handleAADErrors(response: any, endpoint: string, jsonBody?: string): Promise<any> {
  return new Promise((resolve, reject) => {
    if (response.error_description.indexOf("AADSTS500133") !== -1 && retryGetMiddletierToken <= 0) {
      retryGetMiddletierToken++;
      getMiddleTierData(endpoint, jsonBody)
        .then((userData) => resolve(userData))
        .catch((error) => reject(error));
    } else {
      dialogFallback()
        .then((dialogResponse) => resolve(dialogResponse))
        .catch((error) => reject(new Error("AAD error, falling back to dialog." + error)));
    }
  });
}
